import { FC, useEffect, useState } from 'react';
import {
  BaseOption,
  CardStatus,
} from '../../../molecules/base-option-card/base-option-card';
import { Button } from '../../../atoms/button/button';
import { CardContainer } from '../../../molecules/card/card-container';
import {
  ProgressWithLabel,
  Spacing,
  Typography,
} from '../../../atoms/progress-with-label/progress-with-label';
import { Timer } from '../../../molecules/timer/timer';
import { Audio } from '../../../molecules/audio/audio';
import { ImageContainer } from '../../../atoms/image-container/image-container';
import { BottomSheet } from '../../../atoms/bottom-sheet/bottom-sheet';
import { OpenEndedTextArea } from '../../../molecules/open-ended-question/open-ended-question';
import {
  getOpenEndedFeedback,
  IOpenEndedFeedbackResponse,
} from '@taleemabad/db';
import styled from '@emotion/styled';

import { MCQCardListComponent } from '../../../molecules/mcq-card-list/mcq-card-list';
import { MSQCardList } from '../../../molecules/msq-card-list/msq-card-list';
import { ITrainingQuestion, QuestionType, MediaType } from '@taleemabad/db';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

interface QuizProps {
  trainingTitle: string;
  startTime?: number;
  question: ITrainingQuestion;
  totalQuestions: number;
  showBottomSheet?: boolean;
  // we cant use the index in question since they are all 1 (something to fix in our db)
  currentQuestionIndex: number;
  scorePercentage: number;
  scoreText: string;
  retakeCount: number;
  keyValuePair?: { [key: string]: string };
  onAnswerCheck?: (
    isCorrect: boolean,
    answer: string,
    questionType: QuestionType,
    score: number,
    feedback?: string,
  ) => void;
  onNext?: () => void;
  bottomSheetOnDismiss?: () => void;
  isDiagnosticAssessment?: boolean;
}

export const Quiz: FC<QuizProps> = ({
  trainingTitle,
  startTime = 60,
  question: originalQuestion,
  totalQuestions,
  currentQuestionIndex,
  scorePercentage,
  scoreText,
  retakeCount,
  showBottomSheet = false,
  keyValuePair = {},
  onAnswerCheck,
  bottomSheetOnDismiss,
  isDiagnosticAssessment = false,
  onNext,
}) => {
  // adjust the indices in the question.answers array, the originalQuestion.answers array is 1-indexed, but mcq/msq options arrays are 0-indexed.
  // therefore, we subtract 1 from each element in the originalQuestion.answers array to align the indices.
  const openEndedPassingMark = 3;
  const openEndedMinLength = 400;
  const question = {
    ...originalQuestion,
    answers:
      originalQuestion.type === QuestionType.OpenEnded
        ? []
        : originalQuestion.answers.map((answer) => answer - 1),
  };
  const [selectedMCQOption, setSelectedMCQOption] = useState<BaseOption | null>(
    null,
  );
  const [selectedMSQOptions, setSelectedMSQOptions] = useState<
    Array<BaseOption>
  >([]);

  const [isAnswerChecked, setIsAnswerChecked] = useState(false);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [openEndedAnswer, setOpenEndedAnswer] = useState<string>('');
  const [openEndedLoading, setOpenEndedLoading] = useState<boolean>(false);
  const [openEndedResponse, setOpenEndedResponse] =
    useState<IOpenEndedFeedbackResponse | null>();
  const [openEndedInputDisabled, setOpenEndedInputDisabled] =
    useState<boolean>(false);
  const [isOpenEndedValid, setIsOpenEndedValid] = useState<boolean>(false);
  const handleOpenEndedValidationChange = (isValid: boolean) => {
    setIsOpenEndedValid(isValid);
  };

  useEffect(() => {
    // we need to reset the state when the user retakes quiz, this logic is needed for trainings with only one question
    setSelectedMCQOption(null);
    setSelectedMSQOptions([]);
    setIsAnswerChecked(false);
    setOpenEndedAnswer('');
    setIsTimeUp(false);
  }, [retakeCount]);

  const handleMCQSelect = (option: BaseOption) => {
    setSelectedMCQOption(option);
  };

  const handleMSQSelect = (option: BaseOption) => {
    setSelectedMSQOptions((prevOptions) => {
      if (prevOptions.find((prevOption) => prevOption.value === option.value)) {
        return prevOptions.filter(
          (prevOption) => prevOption.value !== option.value,
        );
      } else {
        return [...prevOptions, option];
      }
    });
  };
  const checkOpenEndedQuestion = async (
    answer: string,
  ): Promise<{ isCorrect: boolean; score: number }> => {
    try {
      setOpenEndedLoading(true);
      setOpenEndedInputDisabled(true);
      const openEndedResponse = await getOpenEndedFeedback(question.id, answer);
      setOpenEndedResponse(openEndedResponse);
      setOpenEndedLoading(false);
      if (
        openEndedResponse &&
        openEndedResponse.score >= openEndedPassingMark
      ) {
        return { isCorrect: true, score: openEndedResponse.score };
      }
      return { isCorrect: false, score: openEndedResponse?.score ?? 0 };
    } catch (e: any) {
      return { isCorrect: false, score: 0 };
    }
  };
  const checkAnswer = async () => {
    setIsAnswerChecked(true);
    let isCorrect = false;
    let answer = '';
    let score = 0;
    if (question.type === QuestionType.Mcq) {
      isCorrect = selectedMCQOption
        ? question.answers.includes(Number(selectedMCQOption.value))
        : false;
      answer = selectedMCQOption ? selectedMCQOption.content : 'none';
    } else if (question.type === QuestionType.Msq) {
      isCorrect =
        selectedMSQOptions.length === question.answers.length &&
        selectedMSQOptions.every((selectedOption) =>
          question.answers.includes(Number(selectedOption.value)),
        );
      answer =
        selectedMSQOptions.length > 0
          ? selectedMSQOptions.map((option) => option.content).join(', ')
          : 'none';
    } else if (question.type === QuestionType.OpenEnded) {
      const result = await checkOpenEndedQuestion(openEndedAnswer);
      isCorrect = result.isCorrect;
      score = result.score;
      answer = openEndedAnswer;
    } else if (question.type === QuestionType.Poll) {
      // Poll type questions are treated as MCQ for compatibility
      isCorrect = selectedMCQOption
        ? question.answers.includes(Number(selectedMCQOption.value))
        : false;
      answer = selectedMCQOption ? selectedMCQOption.content : 'none';
    }
    onAnswerCheck?.(isCorrect, answer, question.type, score, openEndedResponse?.feedback);
  };

  const handleTimeUp = () => {
    if (isAnswerChecked || QuestionType.OpenEnded) {
      return;
    }
    setIsTimeUp(true);
    checkAnswer();
  };

  const handleButtonClick = () => {
    if (isAnswerChecked) {
      onNext?.();
    } else {
      checkAnswer();
    }
  };

  const createOption = (
    option: string,
    index: number,
    isSelectedFunc: (index: number) => boolean,
    groupName = '',
  ) => {
    const isSelected = isSelectedFunc(index);
    const isCorrect = question.answers.includes(index);
    let status;
    if (isAnswerChecked) {
      if (isSelected && isCorrect) {
        status = CardStatus.Correct;
      } else if (isSelected) {
        status = CardStatus.Incorrect;
      } else {
        status = CardStatus.Disabled;
      }
    } else {
      status = isSelected ? CardStatus.Selected : CardStatus.Unselected;
    }
    return {
      option: { value: index.toString(), content: option },
      groupName,
      status,
    };
  };

  const createMCQOption = (option: string, index: number) => {
    return createOption(
      option,
      index,
      (index) => Number(selectedMCQOption?.value) === index,
      `group-${question.id}`,
    );
  };

  const createMSQOption = (option: string, index: number) => {
    return createOption(
      option,
      index,
      (index) =>
        !!selectedMSQOptions.find(
          (selectedOption) => Number(selectedOption.value) === index,
        ),
    );
  };
  const openEndedOnChange = (value: string) => {
    setOpenEndedAnswer(value);
  };

  const renderQuestionType = () => {
    switch (question.type) {
      case QuestionType.Mcq:
        return (
          <MCQCardListComponent
            options={question.options.map(createMCQOption)}
            onSelect={handleMCQSelect}
          />
        );
      case QuestionType.Msq:
        return (
          <MSQCardList
            options={question.options.map(createMSQOption)}
            onSelect={handleMSQSelect}
          />
        );
      case QuestionType.OpenEnded:
        return (
          <>
            <OpenEndedTextArea
              value={openEndedAnswer}
              onChange={openEndedOnChange}
              placeholder="Type your answer here..."
              isLoading={openEndedLoading}
              inputDisabled={openEndedInputDisabled}
              minLength={openEndedMinLength}
              onValidationChange={handleOpenEndedValidationChange}
              maxLength={1000}
            />
            <Typography variant="textBodyMediumPrimary">
              Please write answer in English
            </Typography>
            <Spacing marginTop="sm" />
          </>
        );
      case QuestionType.Poll:
        return (
          <MCQCardListComponent
            options={question.options.map(createMCQOption)}
            onSelect={handleMCQSelect}
          />
        );
      default:
        return null;
    }
  };

  const renderQuestionTypeText = (type: QuestionType) => {
    switch (type) {
      case QuestionType.Mcq:
        return 'Multiple Choice Questions';
      case QuestionType.Msq:
        return 'Multiple Select Questions';
      case QuestionType.OpenEnded:
        return 'Open Ended Question';
      case QuestionType.Poll:
        return 'Poll Question';
      default:
        return 'Question';
    }
  };

  const getButtonText = () => {
    if (isAnswerChecked && currentQuestionIndex === totalQuestions - 1) {
      return 'Finish';
    } else if (isAnswerChecked) {
      return 'Next';
    } else {
      return 'Check';
    }
  };

  const isButtonDisabled = () => {
    if (question.type === QuestionType.OpenEnded) {
      if (openEndedLoading) {
        return true;
      }
      return !isOpenEndedValid;
    }
    return (
      !isTimeUp &&
      ((question.type === QuestionType.Mcq && !selectedMCQOption) ||
        (question.type === QuestionType.Msq && selectedMSQOptions.length === 0))
    );
  };
  const getHintForSelectedOption = () => {
    let selectedOptionText = '';

    if (question.type === QuestionType.Mcq && selectedMCQOption) {
      selectedOptionText = selectedMCQOption.content;
    } else if (
      question.type === QuestionType.Msq &&
      selectedMSQOptions.length > 0
    ) {
      selectedOptionText = selectedMSQOptions[0].content;
    }

    const hint = keyValuePair[selectedOptionText];
    if (!hint) {
      return null;
    }
    return hint.replace(/^[A-D]:\s*/, '').trim();
  };
  return (
    <QuizContainer>
      <HeadingContainer>
        <Typography
          data-cy="teacher-traning-quiz-training-title"
          variant="textTitleLargePrimary"
        >
          {trainingTitle}
        </Typography>
        {question.type !== QuestionType.OpenEnded && (
          <Timer
            startTime={startTime}
            retakeCount={retakeCount}
            pause={isAnswerChecked}
            onTimeUp={handleTimeUp}
          ></Timer>
        )}
      </HeadingContainer>

      <QuestionInfoContainer>
        <QuestionTypeContainer>
          <Typography
            data-cy="teacher-training-quiz-question-type"
            variant="textTitleMediumSecondary"
          >
            {renderQuestionTypeText(question.type)}
          </Typography>
          <Spacing marginTop="sm">
            <Typography
              data-cy="teacher-training-quiz-question-number"
              variant="textTitleMediumSurface"
            >
              QUESTION {currentQuestionIndex + 1} OF {totalQuestions}
            </Typography>
          </Spacing>
        </QuestionTypeContainer>
        {!isDiagnosticAssessment && (
          <ProgressWithLabel value={scorePercentage} text={scoreText} />
        )}
      </QuestionInfoContainer>
      <CardContainer variant="filled" minHeight="unset">
        <Spacing padding="xl">
          <Typography
            data-cy="teacher-training-quiz-question-statement"
            variant="textTitleMediumPrimary"
          >
            {question.questionStatement}
          </Typography>
          {question.statementMediaAsset &&
            (question.statementMediaAsset.type === MediaType.Audio ? (
              <Audio isPlaying={true} src={question.statementMediaAsset.url} />
            ) : (
              <ImageContainer
                src={question.statementMediaAsset.url}
                width="100%"
                height="220px"
                maxWidth="450px"
              />
            ))}
        </Spacing>
      </CardContainer>

      <Spacing marginTop="xxl" marginBottom="md">
        {renderQuestionType()}
      </Spacing>
      <Spacing marginTop="xl" />
      {((question.hint &&
        showBottomSheet &&
        getHintForSelectedOption() !== null) ||
        openEndedResponse) && (
        <CardContainer variant="filled" minHeight="unset">
          <Spacing padding="md">
            <Typography
              data-cy="teacher-training-quiz-question-hint"
              variant="textTitleMediumPrimary"
            >
              Feedback
            </Typography>
            <Spacing marginTop="md" />
            <Typography
              data-cy="teacher-training-quiz-question-hint-text"
              variant="textBodyMediumPrimary"
            >
              {getHintForSelectedOption()}
              {openEndedResponse?.feedback}
            </Typography>
          </Spacing>
        </CardContainer>
      )}
      <Spacing marginTop="xl" />
      <ButtonContainer>
        <Button
          icon={isAnswerChecked ? '' : 'check'}
          width="100%"
          disabled={isButtonDisabled()}
          onClick={handleButtonClick}
          data-cy="teacher-training-quiz-button"
        >
          {getButtonText()}
        </Button>
      </ButtonContainer>
    </QuizContainer>
  );
};

const HeadingContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const QuestionInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--rk-spacing-xl);
  margin-bottom: var(--rk-spacing-xxl);
`;

const QuestionTypeContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const QuizContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ButtonContainer = styled.div`
  display: flex;
  ${mq(BreakpointSizes.desktop)} {
    position: relative;
    align-self: flex-end;
    width: fit-content;
  }
  position: relative;
  z-index: 99999999;
`;
